import _ from 'lodash'
import { fromJS, OrderedMap, Map } from 'immutable'
import ArcUUID from '~/util/arc-identifiers'
import BaseConverter from './BaseConverter'
import { ToHTMLConverter, middlewares } from '../util/toHTML'
import { sanitizeText, createEmptyBlock } from '../util/draftUtils'

const ANS_LIST_TYPE_ORDERED = 'ordered'
const ANS_LIST_TYPE_UNORDERED = 'unordered'
const DRAFT_LIST_TYPE_ORDERED = 'ordered-list-item'
const DRAFT_LIST_TYPE_UNORDERED = 'unordered-list-item'

const getAnsItem = (block, htmlConverter) => ({
  type: 'text',
  content: htmlConverter.convert(block, true),
  _id: block.getData().get('id') || ArcUUID.create(),
  // eslint-disable-next-line @typescript-eslint/camelcase
  additional_properties: block
    .getData()
    .get('additionalProperties', Map())
    .toJS(),
  // eslint-disable-next-line @typescript-eslint/camelcase
  block_properties: block
    .getData()
    .get('blockProperties', Map())
    .toJS(),
})

const getAnsList = (block) => ({
  type: 'list',
  // eslint-disable-next-line @typescript-eslint/camelcase
  list_type:
    block.getType() === DRAFT_LIST_TYPE_ORDERED
      ? ANS_LIST_TYPE_ORDERED
      : ANS_LIST_TYPE_UNORDERED,
  items: [],
})

export const _buildAnsItems = (
  blocks,
  items,
  htmlConverter,
  currentIndex = 0,
  currentDepth = -1
) => {
  let currentBlock
  while ((currentBlock = blocks[currentIndex])) {
    if (currentBlock.getDepth() === currentDepth) {
      items.push(getAnsItem(currentBlock, htmlConverter))
      currentIndex++
    } else if (currentBlock.getDepth() > currentDepth) {
      // Create empty list blocks for increased level
      items.push(getAnsList(currentBlock))
      // build next level list
      currentIndex = _buildAnsItems(
        blocks,
        _.last(items).items,
        htmlConverter,
        currentIndex,
        currentDepth + 1
      )
    } else {
      break
    }
  }

  return currentIndex
}

const findFirstValue = (blockMataDatas, key) =>
  (blockMataDatas.find((ans) => ans[key] !== void 0) || {})[key]

export default class ListConverter extends BaseConverter {
  constructor() {
    super('list', [DRAFT_LIST_TYPE_ORDERED, DRAFT_LIST_TYPE_UNORDERED])
    this._htmlConverter = new ToHTMLConverter()
    this._htmlConverter.addMiddleware(
      new middlewares.HighlightToHTMLConverterMiddleware()
    )
    this._htmlConverter.addDefaultMiddleware()
  }

  shouldAggregate(blocks = [], nextBlock) {
    const lastBlock = _.last(blocks)
    // Do not aggregate if no next block or next block is not of list type
    if (
      !nextBlock ||
      _.indexOf(this.getDraftTypes(), nextBlock.getType()) === -1
    ) {
      return false
    }
    // List blocks should be aggregated if the next block has the same list type
    // If list block is a first block in the document (null lastBlock), we should aggregate
    const sameBlockType =
      !lastBlock || nextBlock.getType() === lastBlock.getType()
    return sameBlockType
  }

  setMetaData(element, blocks, context) {
    // just do nothing as we already set data for each block including ansId
    return blocks
  }

  appendAndGetBlocks(element, context) {
    const blocks = []
    if (element) {
      const ansId = element._id
      const listType =
        element.list_type === ANS_LIST_TYPE_ORDERED
          ? DRAFT_LIST_TYPE_ORDERED
          : DRAFT_LIST_TYPE_UNORDERED
      const buildListProps = {
        blocks,
        context,
        ansId,
        alignment: element.alignment,
        channels: element.channels,
      }
      this.buildDraftListContent(
        buildListProps,
        element.additional_properties,
        listType,
        element.items,
        0
      )
    }
    return blocks
  }

  populateMetaData(ansData, blocks) {
    if (ansData) {
      const blockMataDatas = blocks.map((block) => block.getData().toJS())
      const parentData = blockMataDatas.find((ans) => !!ans.ansId) || {}
      // Alignment in list is not synced when new list items are added or pasted
      // so find the first set aligment from all items
      parentData.alignment = findFirstValue(blockMataDatas, 'alignment')
      // Same for channels, find the first set channels from all items
      parentData.channels = findFirstValue(blockMataDatas, 'channels')
      return Object.assign({}, ansData, this.withDefaultData(parentData))
    }

    return ansData
  }

  toAnsDataFromBlocks(contentState, blocks) {
    const ansHolder = []
    _buildAnsItems(blocks, ansHolder, this._htmlConverter)
    return ansHolder[0]
  }

  buildDraftListContent(
    buildListProp,
    additionalProperties,
    listType,
    items,
    depth
  ) {
    const {
      blocks,
      context,
      ansId,
      alignment = '',
      channels = null,
    } = buildListProp
    for (const item of items) {
      if (item.type === 'text') {
        const blockFromHTML = context.toDraftBlock(
          sanitizeText(item.content),
          item,
          [createEmptyBlock()]
        )
        if (blockFromHTML.contentBlocks) {
          const blockWithData = blockFromHTML.contentBlocks.map((block) =>
            block.merge(
              OrderedMap({
                depth,
                type: listType,
              })
            )
          )
          const listBlock = context.appendBlock(blockWithData)[0]
          const metaData = {}
          metaData.id = item._id
          metaData.ansId = ansId
          metaData.alignment = alignment
          metaData.channels = channels
          if (item.additional_properties) {
            metaData.additionalProperties = item.additional_properties
          }
          blocks.push(
            context.setAnsElementMetaData(listBlock.getKey(), fromJS(metaData))
          )
        }
      } else if (item.type === 'list') {
        const listType =
          item.list_type === ANS_LIST_TYPE_ORDERED
            ? DRAFT_LIST_TYPE_ORDERED
            : DRAFT_LIST_TYPE_UNORDERED
        this.buildDraftListContent(
          buildListProp,
          item.additional_properties,
          listType,
          item.items,
          depth + 1
        )
      }
    }
  }
}
